import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { AlarmType } from '../../../shared/components/alarm-card/enums/alarm-type';
import { HistoryItemType } from '../../../shared/components/timeline/enums/history-item-type';

import { useLazyGetAlarm, useLazyGetEmergencyAlarm } from 'features/history/domain/queries/history-queries';
import { HistoryItem } from '../domain/models/history-item';
import ReadAlarmResponse from '../domain/models/read-alarm-response';
import ReadEmergencyAlarmResponse from '../domain/models/read-emergency-alarm-response';

export interface IHistoryProviderHook {
  currentSelectedItem?: HistoryItem;
  setCurrentSelectedItem: Dispatch<SetStateAction<HistoryItem | undefined>>;
  readAlarmData?: ReadAlarmResponse;
  readAlarmIsFetching: boolean;
  readAlarmError?: Error;
  isAlarmDataLoading: () => boolean;
  refresh: () => void;
  setRefreshHistory: (refreshHistory: () => void) => void;

  readEmergencyAlarmData?: ReadEmergencyAlarmResponse;
  readEmergencyAlarmIsFetching: boolean;
  readEmergencyAlarmError?: Error;
}

const useHistoryProvider = (): IHistoryProviderHook => {
  const [currentSelectedItem, setCurrentSelectedItem] = useState<HistoryItem | undefined>(undefined);
  const refreshHistoryRef = useRef<() => void>(() => undefined);

  const {
    refetch: triggerReadAlarm,
    data: readAlarmData,
    isFetching: readAlarmIsFetching,
    error: readAlarmError,
  } = useLazyGetAlarm(currentSelectedItem?.id ?? '');

  const {
    refetch: triggerReadEmergencyAlarm,
    data: readEmergencyAlarmData,
    isFetching: readEmergencyAlarmIsFetching,
    error: readEmergencyAlarmError,
  } = useLazyGetEmergencyAlarm(currentSelectedItem?.id ?? '');

  useEffect(() => {
    readAlarmAndEmergencyAlarm();
  }, [currentSelectedItem]);

  const readAlarmAndEmergencyAlarm = () => {
    if (currentSelectedItem === undefined) {
      return;
    }

    refreshHistoryRef.current();

    if (currentSelectedItem.historyType === HistoryItemType.Alarm && currentSelectedItem.type === AlarmType.EmergencyAlarm) {
      triggerReadEmergencyAlarm();
    } else if (currentSelectedItem.historyType === HistoryItemType.Alarm) {
      triggerReadAlarm();
    }
  };

  const isAlarmDataLoading = (): boolean => {
    if (currentSelectedItem?.type === AlarmType.EmergencyAlarm) {
      return readEmergencyAlarmIsFetching || !readEmergencyAlarmData || readEmergencyAlarmData.id !== currentSelectedItem.id;
    }
    return !currentSelectedItem || readAlarmIsFetching || !readAlarmData || readAlarmData.id !== currentSelectedItem.id;
  };

  const setRefreshHistory = useCallback((refreshHistory: () => void): void => {
    refreshHistoryRef.current = refreshHistory;
  }, []);

  return {
    currentSelectedItem,
    setCurrentSelectedItem,
    readAlarmData,
    readAlarmIsFetching,
    readAlarmError: readAlarmError ?? undefined,
    isAlarmDataLoading,
    refresh: readAlarmAndEmergencyAlarm,
    setRefreshHistory,
    readEmergencyAlarmData,
    readEmergencyAlarmIsFetching,
    readEmergencyAlarmError: readEmergencyAlarmError ?? undefined,
  };
};

export default useHistoryProvider;
